import type { PropertyType } from "@/src/types/propertyApi.types";

export const PROPERTY_TYPE_VALUES = [
  "SINGLE_FAMILY_HOME",
  "TOWNHOME",
  "CONDOMINIUM",
  "APARTMENT",
  "DUPLEX",
  "MULTI_FAMILY",
  "VACATION_HOME",
  "RENTAL_PROPERTY",
  "OFFICE",
  "WAREHOUSE",
  "INDUSTRIAL",
  "MARINA",
  "RV",
  "BOAT",
  "YACHT",
] as const satisfies readonly PropertyType[];

export const PROPERTY_TYPE_OPTIONS: Array<{ value: PropertyType; label: string }> = [
  { value: "SINGLE_FAMILY_HOME", label: "Single Family Home" },
  { value: "TOWNHOME", label: "Townhome" },
  { value: "CONDOMINIUM", label: "Condominium" },
  { value: "APARTMENT", label: "Apartment" },
  { value: "DUPLEX", label: "Duplex" },
  { value: "MULTI_FAMILY", label: "Multi-Family" },
  { value: "VACATION_HOME", label: "Vacation Home" },
  { value: "RENTAL_PROPERTY", label: "Rental Property" },
  { value: "OFFICE", label: "Office" },
  { value: "WAREHOUSE", label: "Warehouse" },
  { value: "INDUSTRIAL", label: "Industrial" },
  { value: "MARINA", label: "Marina" },
  { value: "RV", label: "RV" },
  { value: "BOAT", label: "Boat" },
  { value: "YACHT", label: "Yacht" },
];

/** Maps deprecated API / RentCast values to the current enum. */
export const LEGACY_PROPERTY_TYPE_MAP: Record<string, PropertyType> = {
  SINGLE_FAMILY: "SINGLE_FAMILY_HOME",
  CONDO: "CONDOMINIUM",
  TOWNHOUSE: "TOWNHOME",
  OTHER: "SINGLE_FAMILY_HOME",
};

export function getPropertyTypeLabel(value: string): string {
  const legacy = LEGACY_PROPERTY_TYPE_MAP[value];
  if (legacy) {
    return PROPERTY_TYPE_OPTIONS.find((option) => option.value === legacy)?.label ?? legacy;
  }

  const match = PROPERTY_TYPE_OPTIONS.find((option) => option.value === value);
  if (match) return match.label;

  return value
    .split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");
}

export function isPropertyType(value: string): value is PropertyType {
  return (PROPERTY_TYPE_VALUES as readonly string[]).includes(value);
}

export function normalizePropertyType(value: string): PropertyType | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.toUpperCase().replace(/[\s-]+/g, "_");
  if (isPropertyType(normalized)) return normalized;
  if (LEGACY_PROPERTY_TYPE_MAP[normalized]) return LEGACY_PROPERTY_TYPE_MAP[normalized];

  return null;
}
